'use client';

import Link from 'next/link';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Logo } from '@/components/Logo';
import { NavLanguagePicker } from '@/components/nav/NavLanguagePicker';
import { NavCountryLinks } from '@/components/nav/NavCountryLinks';
import { NavCityLinks } from '@/components/nav/NavCityLinks';
import { NavBrowseLinks } from '@/components/nav/NavBrowseLinks';
import { MadLibsNav } from '@/components/nav/MadLibsNav';
import { uiFb } from '@/lib/i18n';
import { useSearchStore } from '@/stores/search-store';
import { useAuth } from '@/hooks/useAuth';
import { useBookmarkStore } from '@/stores/bookmark-store';
import { BookmarksPanel } from '@/components/features/BookmarksPanel';
import { NavThemePicker } from '@/components/nav/NavThemePicker';

function getDefaultLinks(lang: string) {
  return [
    { href: '#calendar', label: uiFb('ui.calendar', 'Calendar', lang) },
  ];
}

interface NavLink {
  href: string;
  label: string;
}

interface LangAlternate {
  lang: string;
  slug: string;
  label: string;
}

interface LandingNavProps {
  navLinks?: NavLink[];
  lang?: string;
  langAlternates?: LangAlternate[];
  activeCountryCode?: string;
  activeCityId?: string;
  showLanguage?: boolean;
  showCountry?: boolean;
  showCity?: boolean;
  showBrowse?: boolean;
  showSearch?: boolean;
  /** 'madlibs' renders the sentence-based nav, 'classic' renders original dropdowns */
  variant?: 'madlibs' | 'classic';
}

type DropdownId = 'entity' | 'country' | 'city' | 'browse';

export function LandingNav({ navLinks, lang = 'en', langAlternates = [], activeCountryCode, activeCityId, showLanguage = true, showCountry = true, showCity = true, showBrowse = true, showSearch = true, variant = 'madlibs' }: LandingNavProps) {
  const links = navLinks ?? getDefaultLinks(lang);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<DropdownId | null>(null);
  const [bookmarksPanelOpen, setBookmarksPanelOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const { user } = useAuth();
  const bookmarkCount = useBookmarkStore((s) => s.bookmarks.size);
  const fetchBookmarks = useBookmarkStore((s) => s.fetchBookmarks);
  const initialized = useBookmarkStore((s) => s.initialized);
  const clearBookmarks = useBookmarkStore((s) => s.clear);

  useEffect(() => {
    if (user && !initialized) {
      fetchBookmarks(user.id);
    }
    if (!user && initialized) {
      clearBookmarks();
    }
  }, [user, initialized, fetchBookmarks, clearBookmarks]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    hamburgerRef.current?.focus();
  }, []);

  const closeDropdowns = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  const toggleDropdown = useCallback((id: DropdownId) => {
    setActiveDropdown(prev => prev === id ? null : id);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      firstLinkRef.current?.focus();
    }
  }, [menuOpen]);

  return (
    <nav className="glass-panel fixed left-0 right-0 top-0 z-50 border-b border-border-subtle">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 md:px-8">
        {/* Logo */}
        <Link href="/" aria-label={uiFb('ui.nav.home', 'Home', lang)}>
          <Logo size="nav" />
        </Link>

        {/* Desktop center: MadLibs or anchor links */}
        {variant === 'madlibs' ? (
          <div className="hidden flex-1 justify-center px-4 md:flex">
            <MadLibsNav
              lang={lang}
              langAlternates={langAlternates}
              activeCountryCode={activeCountryCode}
            />
          </div>
        ) : (
          <div className="hidden items-center gap-6 md:flex">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-text-secondary underline-offset-4 transition-colors hover:text-text-primary hover:underline"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}

        {/* Right side: dropdowns + CTA + hamburger */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Desktop dropdowns (classic variant only) */}
          <div className="hidden items-center gap-1 md:flex">
            {variant === 'classic' && (
              <>
                {showLanguage && (
                  <NavLanguagePicker
                    langAlternates={langAlternates}
                    currentLang={lang}
                    isOpen={activeDropdown === 'entity'}
                    onToggle={() => toggleDropdown('entity')}
                    onClose={closeDropdowns}
                  />
                )}
                {showCountry && (
                  <NavCountryLinks
                    currentLang={lang}
                    activeCountryCode={activeCountryCode}
                  />
                )}
                {showCity && (
                  <NavCityLinks
                    currentLang={lang}
                    isOpen={activeDropdown === 'city'}
                    onToggle={() => toggleDropdown('city')}
                    onClose={closeDropdowns}
                    activeCityId={activeCityId}
                  />
                )}
                {showBrowse && (
                  <NavBrowseLinks
                    isOpen={activeDropdown === 'browse'}
                    onToggle={() => toggleDropdown('browse')}
                    onClose={closeDropdowns}
                    lang={lang}
                  />
                )}
              </>
            )}
            {showSearch && (
              <button
                onClick={() => useSearchStore.getState().open()}
                aria-label={uiFb('ui.command_palette', 'Search (Ctrl+K)', lang)}
                className="rounded-lg px-2.5 py-1.5 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
            <NavThemePicker lang={lang} />
            {user && (
              <button
                onClick={() => setBookmarksPanelOpen(true)}
                aria-label={uiFb('ui.saved', 'Saved', lang) + (bookmarkCount > 0 ? ` (${bookmarkCount})` : '')}
                className="relative rounded-lg px-2.5 py-1.5 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {bookmarkCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-semantic-error px-1 text-caption font-bold text-white">
                    {bookmarkCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* CTA -- desktop/tablet */}
          <a
            href="#countries"
            className="btn-brand-gradient hidden items-center gap-1 rounded-xl px-4 py-2.5 text-xs font-semibold shadow-sm md:inline-flex"
          >
            {uiFb('ui.open_full_calendar', 'Open Full Calendar', lang)} <span aria-hidden="true">&rarr;</span>
          </a>

          {/* Hamburger -- mobile only */}
          <button
            ref={hamburgerRef}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? uiFb('ui.close_menu', 'Close menu', lang) : uiFb('ui.open_menu', 'Open menu', lang)}
            aria-expanded={menuOpen}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-text-primary md:hidden"
          >
            {menuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="4" x2="16" y2="16" /><line x1="16" y1="4" x2="4" y2="16" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="5" x2="17" y2="5" /><line x1="3" y1="10" x2="17" y2="10" /><line x1="3" y1="15" x2="17" y2="15" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div
          className="border-t border-border-subtle bg-surface-elevated px-4 pb-5 pt-3 md:hidden"
          onKeyDown={(e) => { if (e.key === 'Escape') closeMenu(); }}
        >
          {/* Section links */}
          <div className="flex flex-col gap-1">
            {links.map((link, i) => (
              <a
                key={link.href}
                ref={i === 0 ? firstLinkRef : undefined}
                href={link.href}
                onClick={closeMenu}
                className="min-h-[44px] rounded-lg px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile navigation */}
          {variant === 'madlibs' ? (
            <div className="mt-3 border-t border-border-subtle pt-3">
              <MadLibsNav
                lang={lang}
                langAlternates={langAlternates}
                activeCountryCode={activeCountryCode}
                mobile
              />
            </div>
          ) : (
            (showLanguage || showCountry || showCity || showBrowse) && (
              <div className="mt-3 flex flex-col gap-1 border-t border-border-subtle pt-3">
                {showLanguage && (
                  <NavLanguagePicker
                    langAlternates={langAlternates}
                    currentLang={lang}
                    isOpen={activeDropdown === 'entity'}
                    onToggle={() => toggleDropdown('entity')}
                    onClose={closeDropdowns}
                  />
                )}
                {showCountry && (
                  <NavCountryLinks
                    currentLang={lang}
                    activeCountryCode={activeCountryCode}
                  />
                )}
                {showCity && (
                  <NavCityLinks
                    currentLang={lang}
                    isOpen={activeDropdown === 'city'}
                    onToggle={() => toggleDropdown('city')}
                    onClose={closeDropdowns}
                    activeCityId={activeCityId}
                  />
                )}
                {showBrowse && (
                  <NavBrowseLinks
                    isOpen={activeDropdown === 'browse'}
                    onToggle={() => toggleDropdown('browse')}
                    onClose={closeDropdowns}
                    lang={lang}
                  />
                )}
              </div>
            )
          )}

          {/* Search -- mobile */}
          {showSearch && (
            <button
              onClick={() => {
                closeMenu();
                useSearchStore.getState().open();
              }}
              className="mt-3 flex min-h-[44px] min-w-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>{uiFb('ui.search_pages', 'Search pages', lang)}</span>
              <kbd className="ml-auto rounded-md border border-border-subtle bg-surface-secondary px-1.5 py-0.5 text-caption text-text-dim">Ctrl+K</kbd>
            </button>
          )}

          {/* Theme picker -- mobile */}
          <div className="mt-3 flex items-center gap-3 rounded-lg px-3 py-1 text-sm font-medium text-text-secondary">
            <span>Theme</span>
            <NavThemePicker lang={lang} />
          </div>

          {/* CTA -- mobile */}
          <a
            href="#countries"
            className="btn-brand-gradient mt-3 flex min-h-[44px] items-center justify-center gap-1 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm"
          >
            {uiFb('ui.open_full_calendar', 'Open Full Calendar', lang)} <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      )}

      <BookmarksPanel open={bookmarksPanelOpen} onClose={() => setBookmarksPanelOpen(false)} />
    </nav>
  );
}
